/**
 * Assembles the toolbar's descriptors into the ordered model the renderers
 * consume, and resolves an item's action.
 *
 * Placement is applied here and nowhere else: `buildMainToolbarModel` walks a
 * `ToolbarLayout`, so B14 hands in the user's layout and B15 re-defaults
 * `DEFAULT_TOOLBAR_LAYOUT` without either touching a render site. The
 * stored-over-default merge lives in `./mergeToolbarLayout`.
 */
import { executeCommand } from '../../command-palette/registry/commandRegistry';
import {
  DEFAULT_TOOLBAR_LAYOUT,
  MORE_GROUP_LABELS,
  TOOLBAR_MORE_ITEM_ID,
  type MainToolbarModel,
  type ToolbarItemDescriptor,
  type ToolbarItemId,
  type ToolbarLayout,
  type ToolbarModelDeps,
} from './toolbarItemTypes';
import { isToolbarItemGateOpen, mergeToolbarLayout } from './mergeToolbarLayout';
import { buildPrimaryToolbarItems } from './toolbarPrimaryItems';
import { buildMoreToolbarItems } from './toolbarMoreItems';
import { buildDbaToolbarItems } from './toolbarDbaItems';

/**
 * The action a click runs. A descriptor may either carry its own `run` (every
 * item does today — the toolbar's handlers seed context and fire `toolbar:*`
 * analytics the registry's palette-oriented commands do not) or declare only a
 * `commandId`, in which case the command registry dispatches it. There is no
 * second dispatch mechanism: registry-declared ids go through
 * `executeCommand`, which owns the unknown-id / gated-off contract.
 */
export function resolveToolbarAction(item: ToolbarItemDescriptor): (() => void) | undefined {
  if (item.run) return item.run;
  const { commandId } = item;
  if (!commandId) return undefined;
  return () => {
    executeCommand(commandId);
  };
}

function indexById(items: ToolbarItemDescriptor[]): Map<ToolbarItemId, ToolbarItemDescriptor> {
  return new Map(items.map((item) => [item.id, item]));
}

function pick(
  catalog: Map<ToolbarItemId, ToolbarItemDescriptor>,
  ids: ToolbarItemId[],
  deps: ToolbarModelDeps,
): ToolbarItemDescriptor[] {
  return ids
    .map((id) => catalog.get(id))
    .filter(
      (item): item is ToolbarItemDescriptor =>
        item !== undefined && item.visible !== false && isToolbarItemGateOpen(item.id, deps.gating),
    );
}

export function buildMainToolbarModel(
  deps: ToolbarModelDeps,
  layout: ToolbarLayout = DEFAULT_TOOLBAR_LAYOUT,
): MainToolbarModel {
  // ONE catalog for both surfaces (ids are unique across them — asserted in
  // the model tests). Two catalogs would silently drop any item the layout
  // moves between the primary row and the menu, which is exactly what B15's
  // "promote Compare" reshuffle does.
  const catalog = indexById([
    ...buildPrimaryToolbarItems(deps),
    ...buildMoreToolbarItems(deps),
    ...buildDbaToolbarItems(deps),
  ]);
  const merged = mergeToolbarLayout(layout);

  const more = merged.more
    .map((entry) => ({
      id: entry.group,
      label: MORE_GROUP_LABELS[entry.group],
      items: pick(catalog, entry.items, deps),
    }))
    // A group whose items are all gated off would otherwise render as a bare
    // label + separator. This is also what closes the DBA group when no
    // connection is eligible — one mechanism (the per-item gate) rather than
    // a second surface-level one that an id could escape by moving.
    .filter((group) => group.items.length > 0);

  return {
    // B14 can promote or hide every menu item at once, which would leave the
    // More ▾ trigger opening an empty dropdown. The anchor policy says the
    // user may not HIDE the trigger (that would strand items); a trigger with
    // nothing behind it strands nothing, so it is simply not rendered.
    primary: pick(catalog, merged.primary, deps).filter(
      (item) => item.id !== TOOLBAR_MORE_ITEM_ID || more.length > 0,
    ),
    more,
  };
}
